import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { TASK_TYPE_LABELS, TASK_STATUS, type TaskType, type TaskStatus } from '@parecer/shared';
import { formatBRL, formatRelativeDate } from '@/lib/utils';

interface TaskItem {
  taskId: string;
  ownerId: string;
  ownerName?: string | null;
  ownerEmail?: string | null;
  lawyerId?: string;
  lawyerName?: string | null;
  lawyerEmail?: string | null;
  type: TaskType;
  status: TaskStatus;
  description: string;
  totalAmount: number;
  createdAt: string;
}

const STATUSES = Object.values(TASK_STATUS);

export function AdminTarefas() {
  const [filter, setFilter] = useState<string>('');
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tasks', filter],
    queryFn: () =>
      api.get<{ items: TaskItem[] }>(`/admin/tasks?limit=200${filter ? `&status=${filter}` : ''}`),
  });

  const filtered = useMemo(() => {
    if (!q) return data?.items ?? [];
    const n = q.toLowerCase();
    return (data?.items ?? []).filter(
      (t) =>
        t.taskId.includes(q) ||
        t.description?.toLowerCase().includes(n) ||
        t.ownerName?.toLowerCase().includes(n) ||
        t.lawyerName?.toLowerCase().includes(n) ||
        t.ownerEmail?.toLowerCase().includes(n) ||
        t.lawyerEmail?.toLowerCase().includes(n),
    );
  }, [data, q]);

  return (
    <AdminShell>
      <div className="max-w-7xl">
        <h1 className="font-serif text-display-lg">Tarefas</h1>
        <p className="text-muted-foreground mt-1">
          {filtered.length} {filter ? 'com este status' : 'no total'}. Click numa linha pra abrir o detalhe completo.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Buscar por ID, descrição, cliente ou advogado…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
          />
          <Button size="sm" variant={filter === '' ? 'accent' : 'outline'} onClick={() => setFilter('')}>
            Todas
          </Button>
          {STATUSES.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? 'accent' : 'outline'}
              onClick={() => setFilter(s)}
            >
              {statusLabel(s)}
            </Button>
          ))}
        </div>

        <Card className="mt-6">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 text-left text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Tipo</th>
                  <th className="p-3 font-medium">Cliente</th>
                  <th className="p-3 font-medium">Advogado</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium text-right">Valor</th>
                  <th className="p-3 font-medium text-right">Criada</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {isLoading && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      Carregando…
                    </td>
                  </tr>
                )}
                {!isLoading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      Nenhuma tarefa.
                    </td>
                  </tr>
                )}
                {filtered.map((t) => (
                  <tr key={t.taskId} className="hover:bg-muted/30 transition-colors">
                    <td className="p-3">
                      <Link
                        to={`/admin/tarefas/${t.taskId}`}
                        className="font-medium text-foreground hover:underline"
                      >
                        {TASK_TYPE_LABELS[t.type]}
                      </Link>
                      <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                        {t.description}
                      </p>
                    </td>
                    <td className="p-3">
                      <p className="font-medium">{t.ownerName ?? '—'}</p>
                      <p className="text-[11px] text-muted-foreground">{t.ownerEmail ?? ''}</p>
                    </td>
                    <td className="p-3">
                      <p className="font-medium">{t.lawyerName ?? '—'}</p>
                      <p className="text-[11px] text-muted-foreground">{t.lawyerEmail ?? ''}</p>
                    </td>
                    <td className="p-3">
                      <Badge variant={statusVariant(t.status)}>{statusLabel(t.status)}</Badge>
                    </td>
                    <td className="p-3 text-right">
                      {t.totalAmount > 0 ? formatBRL(t.totalAmount) : '—'}
                    </td>
                    <td className="p-3 text-right text-muted-foreground text-xs">
                      {formatRelativeDate(t.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}

function statusLabel(s: string): string {
  return (
    {
      open: 'Aberta',
      assigned: 'Atribuída',
      in_progress: 'Em andamento',
      delivered: 'Entregue',
      completed: 'Concluída',
      rejected: 'Rejeitada',
      cancelled: 'Cancelada',
      disputed: 'Em disputa',
    }[s] ?? s
  );
}

function statusVariant(s: string): 'default' | 'success' | 'warning' | 'danger' | 'muted' {
  if (s === 'completed' || s === 'delivered') return 'success';
  if (s === 'open' || s === 'assigned' || s === 'in_progress') return 'warning';
  if (s === 'disputed') return 'danger';
  if (s === 'cancelled' || s === 'rejected') return 'muted';
  return 'default';
}
